<template>
    <div>
        <!-- Terms Acceptance Modal -->
        <terms-acceptance-popup 
            :show="showTermsModal" 
            :subscription-type="subscriptionType"
            :no-flash="true"
            :is-processing="isSubscribing && keepTermsModalOpen"
            @close="closeTermsModal"
            @success="handleTermsSuccess"
            @error="handleTermsError"
        ></terms-acceptance-popup>

        <!-- Add Credit Card Modal -->
        <add-card-popup
            :show="showAddCardModal"
            :stripe_data="stripeData"
            confirm-button-text="Subscribe"
            @card-added="handleCardAdded"
            @close="closeCardModal"
        ></add-card-popup>
    </div>
</template>

<script setup>
import { ref, nextTick } from 'vue';
import { router } from '@inertiajs/vue3';
import axios from 'axios';
import TermsAcceptancePopup from '@/Shared/TermsAcceptancePopup.vue';
import AddCardPopup from '@/Shared/AddCardPopup.vue';

const props = defineProps({
    subscriptionType: {
        type: String,
        default: 'protect'
    },
    termsAccepted: {
        type: Boolean,
        default: false
    },
    hasPaymentMethod: {
        type: Boolean,
        default: false
    },
    userPaymentMethod: {
        type: Object,
        default: null
    },
    stripeData: {
        type: [Object, Array],
        required: true
    },
    // RAC-10025: confirmation modal removed. Prop kept for backward compat
    // with existing callers; value is ignored. Safe to drop in a follow-up
    // PR once every caller has been cleaned up.
    showConfirmationModal: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['success', 'error', 'cancel']);

// Reactive state
const showTermsModal = ref(false);
const showAddCardModal = ref(false);
const isSubscribing = ref(false);
const pendingApplication = ref(null);
const keepTermsModalOpen = ref(false); // Flag to keep terms modal open until subscription succeeds

/**
 * Get the prop name for terms accepted based on subscription type
 */
const getTermsAcceptedProp = () => {
    const propMap = {
        'protect': 'protect_tos_accepted_at',
        'rent_payments': 'rent_payments_tos_accepted_at',
        'maintenance': 'maintenance_tos_accepted_at'
    };
    return propMap[props.subscriptionType] || 'protect_tos_accepted_at';
};

/**
 * Get the prop names for payment method based on subscription type
 */
const getPaymentMethodProps = () => {
    return ['is_agent_payment_method_added', 'user_payment_method'];
};

/**
 * Get the prop names for subscription status based on subscription type
 */
const getSubscriptionProps = () => {
    const baseProps = ['hasProtectAccess', 'is_new_agent', 'is_agent_payment_method_added', 'user_payment_method'];
    baseProps.push(getTermsAcceptedProp());
    return baseProps;
};

const closeAllModals = () => {
    showTermsModal.value = false;
    showAddCardModal.value = false;
    keepTermsModalOpen.value = false;
};

/**
 * Main method to initiate the subscription flow
 * This will check terms, card, and subscribe if needed
 *
 * RAC-10025: removed the post-add-card confirmation modal. When the user
 * already has a card + accepted terms, we subscribe immediately. When the
 * add-card flow runs, the AddCardPopup CTA reads "Subscribe" and clicking
 * it both attaches the card AND subscribes — no intermediate confirmation.
 *
 * @param {Object} application - Optional application to navigate to after subscription
 */
const initiateSubscription = async (application = null) => {
    pendingApplication.value = application;
    isSubscribing.value = true;
    keepTermsModalOpen.value = false; // Reset flag at start of flow

    // Case 1: User has card and terms already accepted → subscribe immediately.
    if (props.hasPaymentMethod && props.termsAccepted) {
        subscribeToProtect();
        return;
    }

    // Case 2: User has card but terms not accepted → show terms, then subscribe
    if (props.hasPaymentMethod && !props.termsAccepted) {
        showTermsModal.value = true;
        isSubscribing.value = false;
        return;
    }

    // Case 3: User doesn't have card → show terms first (if needed), then add card
    if (!props.hasPaymentMethod && !props.userPaymentMethod) {
        // Only show terms if not already accepted
        if (!props.termsAccepted) {
            showTermsModal.value = true;
        } else {
            // Terms already accepted, go straight to add card
            // Ensure we have valid stripe data before showing card modal
            if (props.stripeData && typeof props.stripeData === 'object' && !Array.isArray(props.stripeData)) {
                showAddCardModal.value = true;
            } else {
                isSubscribing.value = false;
                emit('error', {
                    type: 'card',
                    error: 'Stripe data is not available. Please refresh the page and try again.'
                });
                return;
            }
        }
        isSubscribing.value = false;
        return;
    }

    // Fallback: if somehow we get here, try to subscribe
    isSubscribing.value = false;
    subscribeToProtect();
};

const closeTermsModal = () => {
    // If flag is set, only allow close if not currently subscribing
    // This prevents accidental closes during subscription but allows cancel
    if (keepTermsModalOpen.value && isSubscribing.value) {
        return;
    }
    // Reset flag when closing
    keepTermsModalOpen.value = false;
    showTermsModal.value = false;
    emit('cancel');
};

const handleTermsSuccess = () => {
    // Store current scroll position before reload
    const currentScrollY = window.scrollY;
    
    // Check if card exists before reload - if it does, set flag to keep modal open
    // This prevents the modal from closing when TermsAcceptancePopup emits 'close'
    const cardExists = props.hasPaymentMethod || props.userPaymentMethod;
    if (cardExists) {
        keepTermsModalOpen.value = true;
        isSubscribing.value = true; // Set subscribing state to prevent accidental close
    }
    
    // Reload to get updated terms_accepted status and payment method status
    router.reload({
        only: [getTermsAcceptedProp(), ...getPaymentMethodProps()],
        preserveState: true,
        preserveScroll: true,
        onSuccess: async () => {
            // Restore scroll position to prevent jumping to top
            await nextTick();
            // Use requestAnimationFrame to ensure scroll happens after all DOM updates
            requestAnimationFrame(() => {
                window.scrollTo(0, currentScrollY);
            });
            
            // After terms are accepted, check if we need to add card or subscribe
            // Note: Props will be updated by parent component after reload
            // We need to check the current state - if no payment method, show card modal
            // Otherwise, proceed to subscribe
            const needsCard = !props.hasPaymentMethod && !props.userPaymentMethod;
            
            if (needsCard) {
                // Need to add card - close terms modal and show card modal
                keepTermsModalOpen.value = false;
                showTermsModal.value = false;
                isSubscribing.value = false;
                // Ensure we have valid stripe data before showing card modal
                if (props.stripeData && typeof props.stripeData === 'object' && !Array.isArray(props.stripeData)) {
                    showAddCardModal.value = true;
                } else {
                    emit('error', {
                        type: 'card',
                        error: 'Stripe data is not available. Please refresh the page and try again.'
                    });
                }
            } else {
                // Card exists, keep terms modal open and proceed to subscribe
                // Flag already set above, just proceed to subscribe
                subscribeToProtect();
            }
        }
    });
};

const handleTermsError = ({ error }) => {
    isSubscribing.value = false;
    keepTermsModalOpen.value = false; // Reset flag on error
    emit('error', {
        type: 'terms',
        error: error
    });
};

const closeCardModal = () => {
    showAddCardModal.value = false;
    isSubscribing.value = false;
    // Only emit cancel if user manually closed, not if it's part of the flow
    // Don't emit flow-ended here as the flow continues after card is added
    emit('cancel');
};

const handleCardAdded = (eventData) => {
    const { payment_method, success } = eventData || {};
    
    if (!success) {
        isSubscribing.value = false;
        emit('error', {
            type: 'card',
            error: eventData.error || 'Failed to add card'
        });
        return;
    }

    // Store current scroll position before reload
    const currentScrollY = window.scrollY;

    // Reload to get updated payment method status
    router.reload({
        only: getPaymentMethodProps(),
        preserveState: true,
        preserveScroll: true,
        onSuccess: async () => {
            // Restore scroll position to prevent jumping to top
            await nextTick();
            // Use requestAnimationFrame to ensure scroll happens after all DOM updates
            requestAnimationFrame(() => {
                window.scrollTo(0, currentScrollY);
            });
            
            // After card is added, proceed to subscribe
            subscribeToProtect();
        },
        onError: () => {
            // Even on error, try to subscribe (card might have been added)
            subscribeToProtect();
        }
    });
};

const subscribeToProtect = async () => {
    try {
        isSubscribing.value = true;

        // RAC-10008: read pending plan selection from sessionStorage so the
        // upsell from RiskGuardProReportLimitModal actually subscribes the
        // user to RiskGuard Pro ($14.99) instead of falling through to the
        // backend's "no plan_slug" default of base RiskGuard ($7.99). The V2
        // SubscriptionProtection already does this; the legacy path didn't,
        // so users who hit "YES, UPGRADE TO PRO" on Pages/Properties/Show
        // were silently downgraded back to the cheaper plan even when the
        // flow appeared to succeed.
        let pendingPlan = null;
        try {
            const stored = sessionStorage.getItem('pending_subscription_plan');
            if (stored) {
                pendingPlan = JSON.parse(stored);
            }
        } catch (e) {
            console.error('Error reading stored plan data:', e);
        }

        // RAC-10335: never subscribe without a plan. Omitting plan_slug used to silently
        // land the user on base RiskGuard; surface an error and let them retry instead.
        // (The backend now also rejects a missing plan.)
        if (!pendingPlan?.planSlug) {
            isSubscribing.value = false;
            emit('error', {
                type: 'plan',
                error: 'We could not start your subscription. Please try again.',
            });
            return;
        }

        const requestData = {
            redirect_url: window.location.href,
            ...(!props.termsAccepted && { terms_accepted: true }),
            plan_slug: pendingPlan.planSlug,
            ...(pendingPlan?.billingCycle && { billing_cycle: pendingPlan.billingCycle }),
            // RAC-10336: the property tied to a feature-page Pro upsell auto-converts to Managed.
            ...(pendingPlan?.propertyId && { property_id: pendingPlan.propertyId }),
        };

        // Use axios instead of router.post to avoid resetting component state
        // Axios will follow redirects automatically, so we'll get the final response
        await axios.post(route('protect-subscription.subscribe'), requestData);

        // RAC-10008: clear sessionStorage now that the upsell plan has been
        // submitted; otherwise a later, unrelated subscribe call could pick
        // up stale data.
        sessionStorage.removeItem('pending_subscription_plan');
        
        // If we get here, subscription was successful
        // Close terms modal if it was kept open
        if (keepTermsModalOpen.value) {
            keepTermsModalOpen.value = false;
            showTermsModal.value = false;
        }
        closeAllModals();
        
        // If we're navigating away (pending application), skip reload to avoid
        // leaving the Inertia progress state hanging during the redirect.
        if (pendingApplication.value) {
            emit('success', {
                pendingApplication: pendingApplication.value
            });
            pendingApplication.value = null;
            isSubscribing.value = false;
            return;
        }

        // Reload to get updated subscription status
        // Use preserveState: true to keep modal open in parent component
        const reloadProps = () => {
            router.reload({
                only: getSubscriptionProps(),
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    emit('success', {
                        pendingApplication: pendingApplication.value
                    });
                    pendingApplication.value = null;
                    isSubscribing.value = false;
                },
                onError: () => {
                    emit('success', {
                        pendingApplication: pendingApplication.value
                    });
                    pendingApplication.value = null;
                    isSubscribing.value = false;
                }
            });
        };
        
        setTimeout(reloadProps, 500);
    } catch (error) {
        isSubscribing.value = false;

        // Reset the flag on error so user can retry
        if (keepTermsModalOpen.value) {
            // Keep modal open on error so user can see what happened
            // The modal will show the error state
        }
        
        // Check if it's a redirect to payment methods (shouldn't happen if card flow worked)
        if (error.response?.status === 302 || error.response?.status === 301) {
            const redirectUrl = error.response?.headers?.location || '';
            if (redirectUrl.includes('payment-methods.create')) {
                emit('error', {
                    type: 'subscription',
                    error: 'Payment method is required. Please add a payment method first.'
                });
                return;
            }
        }
        
        const errorMessage = error.response?.data?.message 
            || error.response?.data?.error 
            || error.message 
            || 'An error occurred while subscribing. Please try again.';
        
        emit('error', {
            type: 'subscription',
            error: errorMessage
        });
    }
};

// Expose the initiateSubscription method for parent component to call via ref
defineExpose({
    initiateSubscription
});
</script>

